<template>

    <fc-input
        v-if="type === 'hidden' || (type === 'text' && controlType === 'hidden')"
        type="hidden"
        :name="name"
        :value="inputValue"
        @update:modelValue="update($event)"
    />

    <div v-else class="fc-outer" :class="outerClass">
        <div :class="wrapperClass" class="fc-wrapper">

            <fc-label class="fc-label"
                      v-if="!isCheckbox && label"
                      :class="labelClass"
                      :required="required"
            >{{ label }}
            </fc-label>

            <div :class="innerClass" class="fc-inner">

                <div v-if="prefix" class="fc-prefix" :class="prefixClass">{{ prefix }}</div>

                <fc-input
                    v-if="isInput()"
                    :type="controlType"
                    :class="controlClass"
                    :name="name"
                    :maxlength="maxlength"
                    :value="inputValue"
                    :placeholder="placeholder"
                    :attributes="inputAttributes"
                    :disabled="disabled"
                    :autocomplete="autocomplete"
                    :min-date="minDate"
                    :is-required="isRequired"
                    @update:modelValue="update($event)"
                    @blur="$emit('blur', $event)"
                />

                <fc-textarea
                    class="fc-input fc-textarea"
                    v-if="type === 'textarea'"
                    :class="inputClass"
                    :name="name"
                    :value="inputValue"
                    :placeholder="placeholder"
                    :attributes="inputAttributes"
                    :disabled="disabled"
                    @update:modelValue="update($event)"
                />

                <fc-editor
                    class="fc-input fc-editor"
                    v-if="type === 'editor'"
                    :class="inputClass"
                    :name="name"
                    :value="inputValue"
                    :placeholder="placeholder"
                    :attributes="inputAttributes"
                    :disabled="disabled"
                    @update:modelValue="update($event)"
                />

                <fc-dropdown
                    v-if="type === 'dropdown'"
                    :class="inputClass"
                    :mode="multiple ? 'tags': 'single'"
                    :options="url ? fetchOptions() : options"
                    :required="required"
                    :searchable="true"
                    :name="name"
                    ref="dropdown"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    :modelValue="inputValue"
                    :filter-results="!url"
                    :resolve-on-load="!!url"
                    :delay="url ? 500 : -1"
                    :canDeselect="false"
                    :canClear="false"
                    noOptionsText="Результатов не найдено"
                    noResultsText="Результатов не найдено"
                    @update:modelValue="update($event)"
                >
                    <template v-slot:option="{ option }">
                        <div class="flex items-center space-x-2">
                            <img v-if="option.img" :src="option.img"
                                 class="object-center object-cover shrink-0 w-7 h-7 rounded">
                            <div class="leading-none">
                                <div>{{ option.label }}</div>
                                <div v-if="option.description" class="text-xs opacity-30">
                                    {{ option.description }}
                                </div>
                            </div>
                        </div>
                    </template>
                </fc-dropdown>
                <input v-if="type === 'dropdown'" :name="name" type="hidden" :value="dropdownValues">

                <fc-radiolist
                    class="fc-radio-list"
                    v-if="type === 'radiolist'"
                    :class="inputClass"
                    :name="name"
                    :value="inputValue"
                    :options="options"
                    :label="label"
                    :hint="hint"
                    :disabled="disabled"
                    @update:modelValue="update($event)"
                />

                <fc-checkboxlist
                    class="fc-checkbox-list"
                    v-if="type === 'checkboxlist'"
                    :class="inputClass"
                    :name="name"
                    :value="inputValue"
                    :options="options"
                    :label="label"
                    :hint="hint"
                    :disabled="disabled"
                    @update:modelValue="update($event)"
                />

                <label v-if="type === 'toggle'" class="flex space-x-2 cursor-pointer">
                    <fc-toggle
                        class="fc-toggle toggle-sm"
                        :class="inputClass"
                        :name="name"
                        :value="inputValue"
                        :label="label"
                        :disabled="disabled"
                        @update:modelValue="update($event)"
                    />

                    <div v-if="label" class="fc-label fc-toggle-label" v-html="label"></div>
                </label>

                <component
                    v-if="type === 'widget'"
                    :is="controlType"
                    :name="name"
                    :label="label"
                    :disabled="disabled"
                    v-model="inputValue"
                    :options="options"
                    @update:modelValue="update($event)"
                ></component>

                <div v-if="suffix" class="fc-suffix" :class="suffixClass">
                    {{ suffix }}
                </div>

            </div>
        </div>

        <fc-hint class="fc-hint" :class="hintClass" v-html="hint"></fc-hint>

        <div v-if="error" :class="messagesClass" class="fc-messages">
            <div :class="messageClass" class="fc-message">
                {{ error }}
            </div>
        </div>

    </div>

</template>
<script>
import FcInput from "./input";
import FcLabel from "./label";
import FcHint from "./hint";
import FcTextarea from "./textarea";
import FcEditor from "./editor";
import FcCheckboxToggle from "./toggle";
import FcRadioList from "./radio-list";
import FcCheckboxList from "./checkbox-list";
import FcDropdown from '@vueform/multiselect';

export default {
    components: {
        'fc-input': FcInput,
        'fc-textarea': FcTextarea,
        'fc-editor': FcEditor,
        'fc-label': FcLabel,
        'fc-hint': FcHint,
        'fc-toggle': FcCheckboxToggle,
        'fc-dropdown': FcDropdown,
        'fc-radiolist': FcRadioList,
        'fc-checkboxlist': FcCheckboxList,
    },
    props: {
        modelValue: null,
        minDate: null,
        type: {
            type: String,
            default: 'text',
        },
        controlType: {
            type: String,
            default: 'text',
        },
        label: {
            type: String,
            default: null,
        },
        name: {
            type: String,
            default: '',
        },
        maxlength: {
            type: Number,
            default: null,
        },
        value: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        hint: {
            type: String,
            default: null,
        },
        suffix: {
            type: String,
            default: null,
        },
        prefix: {
            type: String,
            default: null,
        },
        outerClass: {
            type: String,
        },
        innerClass: {
            type: String,
        },
        wrapperClass: {
            type: String,
        },
        timezone: {
            type: String,
        },
        inputClass: {
            type: String,
        },
        inputAttributes: {},
        hintClass: {
            type: String,
        },
        messagesClass: {
            type: String,
        },
        messageClass: {
            type: String,
        },
        prefixClass: {
            type: String,
        },
        suffixClass: {
            type: String,
        },
        labelClass: {
            type: String,
        },
        error: {
            type: String,
            default: null,
        },
        options: {
            type: Object,
            default: []
        },
        multiple: {
            type: Boolean,
            default: false
        },
        grouped: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autocomplete: {
            type: String,
            default: null
        },
        url: {
            type: String,
            default: null
        },
        required: {
            type: Boolean,
            default: false,
        },
        isRequired: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            val: this.value,
            messages: [],
        }
    },
    methods: {
        update(value) {
            this.val = value;
            this.$emit('update:modelValue', value);
        },
        isInput() {
            return ['text', 'hidden', 'password'].includes(this.type);
        },
        fetchOptions() {
            return async (query) => {
                let selected = this.inputValue;
                return (await axios.post(this.url, {
                    term: query,
                    selected: selected
                })).data;
            }
        },
        open() {
            if (this.$refs.dropdown) {
                this.$refs.dropdown.activate();
            }

            return true;
        }
    },
    computed: {
        controlClass() {
            return 'fc-input';
        },
        inputValue() {
            if (this.multiple) {
                if (typeof this.modelValue !== 'object') {
                    return this.modelValue ? this.modelValue.split(';') : [];
                }
            }

            return this.modelValue ?? this.val;
        },
        isWidget() {
            return this.type === 'widget';
        },
        isCheckbox() {
            return this.type === 'toggle';
        },
        needToShowhint() {
            return this.hint && !this.isCheckbox;
        },
        dropdownValues() {
            if (this.multiple) {
                if (typeof this.inputValue === 'object') {
                    return this.inputValue.join(';')
                }
            }

            return this.inputValue;
        },
    }
}
</script>
